/**
 * Which documents a dispatch is expected to carry, and whether it has them.
 *
 * Checklist item 52: "Pending Dispatch - Document missing name to be shown.
 * If all 3 available, the all clear." The upload picker offers Payment proof,
 * LR / e-way bill and a catch-all "Other", while the invoice is attached
 * elsewhere and filtered out of the visible list — so nothing on screen could
 * answer "what is still missing?". This names the three the client means.
 *
 * If their trio is different (e.g. POD instead of invoice), change
 * REQUIRED_DISPATCH_DOCS and nothing else.
 */
export type DispatchDocKind = 'invoice' | 'lr' | 'payment_proof' | 'pod' | 'other';

export interface DispatchDocLike {
  kind: string;
  fileUrl?: string;
  originalName?: string;
}

export interface DispatchDocCheck {
  kind: DispatchDocKind;
  label: string;
  present: boolean;
  files: string[];
}

export const REQUIRED_DISPATCH_DOCS: ReadonlyArray<{ kind: DispatchDocKind; label: string }> = [
  { kind: 'invoice', label: 'Invoice' },
  { kind: 'lr', label: 'LR / e-way bill' },
  { kind: 'payment_proof', label: 'Payment proof' },
];

/**
 * A file counts only when it actually points somewhere. An entry with a kind
 * and an empty URL is a failed upload, not a delivered document.
 */
export function dispatchDocChecklist(docs: DispatchDocLike[] | null | undefined): DispatchDocCheck[] {
  const usable = (docs ?? []).filter((d) => typeof d?.fileUrl === 'string' && d.fileUrl.trim().length > 0);
  return REQUIRED_DISPATCH_DOCS.map(({ kind, label }) => {
    const files = usable.filter((d) => d.kind === kind).map((d) => d.originalName || d.fileUrl!.split('/').pop() || kind);
    return { kind, label, present: files.length > 0, files };
  });
}

export const dispatchDocsAllClear = (docs: DispatchDocLike[] | null | undefined): boolean =>
  dispatchDocChecklist(docs).every((c) => c.present);

export const missingDispatchDocs = (docs: DispatchDocLike[] | null | undefined): DispatchDocCheck[] =>
  dispatchDocChecklist(docs).filter((c) => !c.present);
